import {
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Box,
  Alert,
  Link,
} from "@mui/material";
import { Formik, ErrorMessage, FormikProps, Form } from "formik";
import {
  PricingType,
  ProductType,
} from "../../models/features/Integration/DmsSetup";
import {
  SetupProfile,
  SetupProfileInitialValues,
  SetupProfileValidationSchema,
} from "../../models/features/SetupProfile/SetupProfile";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import React from "react";
import { setSyncExisting } from "../../redux/reducer/features/Auth/authSlice";
import {
  useGetDmsSetupByIdMutation,
  useUpdateSetupProfileMutation,
  useVerifyAccessTokenMutation,
} from "../../redux/reducer/features/Integration/DmsSetupSlice";
import LinkIcon from "@mui/icons-material/Link";
import KeyIcon from "@mui/icons-material/Key";
import { AppState } from "../../redux/reducer/rootReducer";
import { usePriceOptions } from "../../services/utilities/utility";
import PageLoader from "../app/PageLoader";
import GridWithNewPrices from "../Integration/Synchronization/Grids/DefaultPriceSetting";

interface SiteDetailsProps {}
export const SiteDetails: React.FunctionComponent<SiteDetailsProps> = (
  props
) => {
  const priceOptions = usePriceOptions();
  const dispatch = useDispatch<Dispatch<any>>();
  const formRef: React.Ref<FormikProps<SetupProfile>> = React.useRef(null);

  const dmsSetup = useSelector((state: AppState) => state.authReducer.dmsSetup);

  const [setupProfileData, setSetupProfileData] = useState<SetupProfile>(
    SetupProfileInitialValues
  );
  const [showVerification, setShowVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [openPriceType, setOpenPriceType] = useState(false);
  const [productType, setProductType] = useState(ProductType.Wholegoods);

  const [
    verifyShopifyCredentialsMutation,
    verifyShopifyCredentialsMutationResult,
  ] = useVerifyAccessTokenMutation();
  const [updateDmsSetupMutation, updateDmsSetupMutationResult] =
    useUpdateSetupProfileMutation();
  const [dmsSetupMutation, dmsSetupMutationResult] =
    useGetDmsSetupByIdMutation();

  useEffect(() => {
    dmsSetupMutation({}).then((response: any) => {
      if (response && response.data && response.data.status === 0) {
        let data = response?.data.entity;
        setSetupProfileData({
          id: data.id,
          shopifyUrl: data.shopifyUrl,
          shopifyAccessToken: data.shopifyAccessToken,
          siteName: data.siteName,
          syncExisting: data.syncExisting,
          IsUpdateCustomerAndPaymentOnly: false,
        });
      }
    });
  }, []);

  const handleOnSubmit = (values: SetupProfile) => {
    setShowVerification(false);
    setVerificationMessage("");
    dispatch(setSyncExisting(values.syncExisting));
    verifyShopifyCredentialsMutation({
      shopUrl: values.shopifyUrl ?? "",
      shopAccessToken: values.shopifyAccessToken ?? "",
    }).then((res: any) => {
      if (res && res.data) {
        if (res.data.status === 0) {
          SaveProfileSetting(values);
        } else {
          setIsVerified(false);
          setVerificationMessage(
            "Your Shopify credentials (Shopify Url OR Access token) are not Valid."
          );
          setShowVerification(true);
        }
      }
    });
  };

  const SaveProfileSetting = (values: SetupProfile) => {
    updateDmsSetupMutation({ data: values }).then((res: any) => {
      if (res && res.data) {
        setIsVerified(true);
        setVerificationMessage("Profile Setting saved successfully.");
        setShowVerification(true);
        let data = res?.data.entity;
        setSetupProfileData({
          id: data.id,
          shopifyUrl: data.shopifyUrl,
          shopifyAccessToken: data.shopifyAccessToken,
          siteName: data.siteName,
          syncExisting: data.syncExisting,
          IsUpdateCustomerAndPaymentOnly: false,
        });
      }
    });
  };

  const handleClose = () => {
    setOpenPriceType(false);
  };

  const handleCloseAlert = () => {
    setShowVerification(false);
  };

  return (
    <>
      <PageLoader isLoading={dmsSetupMutationResult.isLoading}></PageLoader>

      <Box sx={{ pb: 2, mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
          Site details
        </Typography>
      </Box>

      <Formik
        initialValues={setupProfileData}
        enableReinitialize={true}
        validationSchema={SetupProfileValidationSchema}
        innerRef={formRef}
        onSubmit={(values) => {
          handleOnSubmit(values);
        }}
      >
        {({ values, handleChange, handleBlur, setFieldValue }) => (
          <>
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    id="siteName"
                    name="siteName"
                    label="Site Name"
                    fullWidth
                    variant="standard"
                    value={values.siteName}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="shopifyUrl"
                    name="shopifyUrl"
                    label="Shopify Url"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.shopifyUrl}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <LinkIcon sx={{ color: "rgb(56, 55, 110)" }} />
                        </InputAdornment>
                      ),
                    }}
                    variant="standard"
                  />
                  <ErrorMessage
                    className="error-msg"
                    component={"div"}
                    name="shopifyUrl"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="shopifyAccessToken"
                    name="shopifyAccessToken"
                    label="Shopify Access Token"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={
                      values.shopifyAccessToken ??
                      "*************************************************************"
                    }
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <KeyIcon sx={{ color: "rgb(56, 55, 110)" }} />
                        </InputAdornment>
                      ),
                    }}
                    variant="standard"
                  />
                  <ErrorMessage
                    className="error-msg"
                    component={"div"}
                    name="shopifyAccessToken"
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={12}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    {showVerification ? (
                      <Alert
                        onClose={handleCloseAlert}
                        severity={isVerified ? "success" : "error"}
                      >
                        {verificationMessage}
                      </Alert>
                    ) : (
                      ""
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </>
        )}
      </Formik>

      <Box sx={{ pb: 2, mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
          Pricing configuration
        </Typography>
      </Box>

      <Box>
        {dmsSetup?.productTypes?.some((type) => type === "Wholegoods") && (
          <Grid item xs={12} display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{ fontWeight: "bold", pt: "5px" }}
              variant="subtitle2"
              gutterBottom
            >
              Wholegoods default price level:
            </Typography>
            {priceOptions
              .filter(
                (x: any) =>
                  x.type === PricingType[dmsSetup?.priceType ?? "Price"]
              )
              .map((option) => (
                <Typography fontSize="0.9rem">{option.label}</Typography>
              ))}
            <Link
              sx={{
                cursor: "pointer",
                fontStyle: "italic",
              }}
              onClick={() => {
                setProductType(ProductType.Wholegoods);
                setOpenPriceType(true);
              }}
            >
              Change
            </Link>
          </Grid>
        )}
        {dmsSetup?.productTypes?.some((type) => type === "Parts") && (
          <Grid item xs={12} display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{ fontWeight: "bold", pt: "5px" }}
              variant="subtitle2"
              gutterBottom
            >
              Parts default price level:
            </Typography>
            {priceOptions
              .filter(
                (x: any) =>
                  x.type === PricingType[dmsSetup?.partsPriceType ?? "Price"]
              )
              .map((option) => (
                <Typography fontSize="0.9rem">{option.label}</Typography>
              ))}
            <Link
              sx={{
                cursor: "pointer",
                fontStyle: "italic",
              }}
              onClick={() => {
                setProductType(ProductType.Parts);
                setOpenPriceType(true);
              }}
            >
              Change
            </Link>
          </Grid>
        )}
      </Box>

      {openPriceType ? (
        <GridWithNewPrices
          handleClose={handleClose}
          isOpen={openPriceType}
          productType={productType}
        />
      ) : (
        <></>
      )}
    </>
  );
};
